import { useEffect, useState } from 'react';
import { fetchCocBilling, fetchPatientBills } from '@/dashboard/api';
import type { CocBillingRowUi } from '@/dashboard/api';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
// BILLING-MISSING-DELTA-V1: the COC read of itemized appointment bills --
// the SAME bill wire the front desk composes (ONE implementation, two role
// surfaces; the COC sheet's H. BILLING rows). Read-only here: composing and
// finalizing stay on the front-desk entry surface; the COC supervises.
import { AcButton } from '@/dashboard/ui';
import BillDetailCard from '@/dashboard/billing/BillDetailCard';
import BillPrintView from '@/dashboard/billing/BillPrintView';
import { formatMinorUnitsInr } from '@/dashboard/billing/billing-entry-view';
import {
  BILL_STATUS_LABELS,
  isBillStoreUnavailableError,
  readPatientBillListWire,
  type PatientBillWireMirror,
} from '@/dashboard/billing/bill-view';

type CocBillsState =
  | { readonly kind: 'loading' }
  /** 20260830_1 unapplied: the honest line (never a dead affordance). */
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'ready'; readonly bills: readonly PatientBillWireMirror[] };

export default function BillingCompliancePage() {
  const [rows, setRows] = useState<CocBillingRowUi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billsState, setBillsState] = useState<CocBillsState>({
    kind: 'loading',
  });
  const [openBillId, setOpenBillId] = useState<number | null>(null);
  const [printingBill, setPrintingBill] =
    useState<PatientBillWireMirror | null>(null);
  const now = new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchCocBilling()
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The itemized bills at the COC's assigned clinics (the server enforces
  // the clinic belt + the billing role question; this page only renders).
  useEffect(() => {
    let cancelled = false;
    setBillsState({ kind: 'loading' });
    fetchPatientBills({ limit: 20 })
      .then((res) => {
        if (cancelled) return;
        // THE TOLERANT BOUNDARY (walk24-2 corrective): the SAME shape proof
        // the front-desk panel runs -- a serving side without the bill
        // contract reads as the honest unavailable line, never a crash.
        const read = readPatientBillListWire(res);
        if (read.kind === 'not_served') {
          setBillsState({ kind: 'unavailable' });
          return;
        }
        setBillsState({ kind: 'ready', bills: read.bills });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (isBillStoreUnavailableError(e)) {
          setBillsState({ kind: 'unavailable' });
        } else {
          setBillsState({
            kind: 'error',
            message: e instanceof Error ? e.message : String(e),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const clinicLabelById = new Map(rows.map((r) => [r.clinicId, r.clinicName]));

  if (printingBill !== null) {
    return (
      <BillPrintView
        bill={printingBill}
        clinicLabel={
          clinicLabelById.get(printingBill.clinicId) ??
          `Clinic #${printingBill.clinicId}`
        }
        backLabel="Back to billing"
        onBack={() => setPrintingBill(null)}
      />
    );
  }

  const submitted = rows.filter((r) => r.submitted);
  const pending = rows.filter((r) => !r.submitted);

  return (
    <div className="billing-page">
      <div className="page-header">
        <h1>Billing compliance</h1>
        <p className="page-header__sub">
          {submitted.length} submitted · {pending.length} pending · as of {now}
        </p>
      </div>

      {error ? (
        <div className="mb-4">
          <DashboardLoadError message={error} />
        </div>
      ) : null}

      {loading ? (
        <div className="loading">Loading billing…</div>
      ) : rows.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th>Clinic</th>
              <th>Status</th>
              <th>Revenue</th>
              <th>Patients</th>
              {/* SHIP-LANE-1 OWNER TRUTH CORRECTIONS (FIX 3, wording
                  parity): this column is `avg_ticket_size` from the SAME MIS
                  rollup the AGM zone reads, beside the SAME billed
                  `patientCount` -- one denominator, one name across screens.
                  "Ticket size" stays the money console's revenue / walk-ins.
                  No denominator changed. */}
              <th>Avg per billed patient</th>
              <th>vs target</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.clinicId}>
                <td>{r.clinicName}</td>
                <td>
                  <span className={`badge ${r.submitted ? 'badge--success' : 'badge--warning'}`}>
                    {r.submitted ? 'submitted' : 'pending'}
                  </span>
                </td>
                <td>₹{r.totalRevenue.toLocaleString('en-IN')}</td>
                <td>{r.patientCount}</td>
                <td>₹{r.avgTicket.toLocaleString('en-IN')}</td>
                <td>{r.belowTarget ? 'Below target' : 'OK'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-muted-foreground">No clinics in scope or no rows for today.</p>
      )}

      {/* BILLING-MISSING-DELTA-V1: recent itemized bills at the assigned
          clinics -- the COC sheet's appointment-billing detail read
          ("A. Appointment details" / lines / units / gross-net / print /
          previous billing), served through the COC's OWN existing door. */}
      <div className="page-header">
        <h1>Recent itemized bills</h1>
        <p className="page-header__sub">
          Appointment bills recorded at your assigned clinics (newest first).
        </p>
      </div>
      {billsState.kind === 'loading' ? (
        <div className="loading" data-testid="coc-bills-loading">
          Loading bills...
        </div>
      ) : billsState.kind === 'unavailable' ? (
        <p className="text-sm text-muted-foreground" data-testid="coc-bills-unavailable">
          Itemized bills are not available on this server yet (the bill
          store&apos;s database change awaits the founder apply).
        </p>
      ) : billsState.kind === 'error' ? (
        <div className="mb-4" data-testid="coc-bills-error">
          <DashboardLoadError message={billsState.message} />
        </div>
      ) : billsState.bills.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="coc-bills-empty">
          No itemized bills recorded at your clinics yet.
        </p>
      ) : (
        <table className="table" data-testid="coc-bills-table">
          <thead>
            <tr>
              <th>Bill</th>
              <th>Clinic</th>
              <th>Patient</th>
              <th>Visit</th>
              <th>Status</th>
              <th>Net amount</th>
              <th>Open</th>
            </tr>
          </thead>
          <tbody>
            {billsState.bills.map((bill) => (
              <tr key={bill.id} data-testid={`coc-bill-row-${bill.id}`}>
                <td>#{bill.id}</td>
                <td>
                  {clinicLabelById.get(bill.clinicId) ??
                    `Clinic #${bill.clinicId}`}
                </td>
                <td>{bill.patientName ?? bill.patientId}</td>
                <td>Appointment #{bill.appointmentId}</td>
                <td>{BILL_STATUS_LABELS[bill.status]}</td>
                <td>{formatMinorUnitsInr(bill.totals.netMinorUnits)}</td>
                <td>
                  <AcButton
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      setOpenBillId(openBillId === bill.id ? null : bill.id)
                    }
                    data-testid={`coc-bill-open-${bill.id}`}
                  >
                    {openBillId === bill.id ? 'Close detail' : 'Open detail'}
                  </AcButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {billsState.kind === 'ready' && openBillId !== null
        ? (() => {
            const open = billsState.bills.find((b) => b.id === openBillId);
            if (open === undefined) return null;
            return (
              <div data-testid="coc-bill-detail">
                <BillDetailCard bill={open} testId={`coc-bill-detail-${open.id}`} />
                <AcButton
                  variant="secondary"
                  size="sm"
                  onClick={() => setPrintingBill(open)}
                  data-testid={`coc-bill-print-${open.id}`}
                >
                  Print bill
                </AcButton>
              </div>
            );
          })()
        : null}
    </div>
  );
}
